/* Wireframe formations — 모델별 데이터 문법.
 * overview 원천 4종 → 패브릭 → XAI/수요/이상 병렬 → PMF 가중합
 * fabric   웹/리뷰/공공/이벤트 → 격자 표준화 → Kafka 레인 → Redis/S3/Milvus
 * xai      텍스트/지표/테이블 → 합류 → 속성 막대 → SHAP 순위
 * forecast 시계열 막대 → lookback → TFT 층 → p10/p50/p90 밴드
 * anomaly  산포 스트림 → AE 병목 → 이상치 → 원인 막대
 * pmf      이미지/텍스트/메타/행동 → 임베딩 구 → 융합 → 가중 위성
 */
import { MODEL_W, PMF_SCORES } from './engine/pmfEngine.js';
export const N = 224;
export const PN = 1400;
export const ACCENT = 0x5980a6;
export const DEEP = 0x1d2d3d;
export const GRAY = 0x98989b;

export function rng(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function mk() { return Array.from({ length: N }, () => ({ p: [0, 0, 0], s: [1, 1, 1], q: [0, 0, 0], c: 1 })); }

function titleFabric() {
  const f = mk();
  f.forEach((d, i) => {
    const layer = i % 5, k = Math.floor(i / 5);
    const cx = k % 8, cy = Math.floor(k / 8);
    d.p = [(cx - 3.5) * 0.85, (layer - 2) * 1.55, (cy - 2.5) * 0.85];
    d.s = [0.7, 0.22, 0.7]; d.q = [0, 0, 0];
    d.c = 0.25 + layer * 0.18;
  });
  return f;
}

function titleXai() {
  const f = mk();
  f.forEach((d, i) => {
    const rank = i % 14, k = Math.floor(i / 14);
    const h = 0.8 + (13 - rank) * 0.42;
    d.p = [(rank - 6.5) * 1.15, h / 2 - 2.2, (k - 7) * 0.32];
    d.s = [0.68, h, 0.68]; d.q = [0, 0, 0];
    d.c = rank < 4 ? 1 : 0.4;
  });
  return f;
}

function titleForecast() {
  const f = mk();
  f.forEach((d, i) => {
    const t = i % 32, row = Math.floor(i / 32);
    const wave = Math.sin(t * 0.38) * 2.4 + t * 0.05;
    d.p = [(t - 15.5) * 0.72, wave + (1.5 - row) * 0.55, 0];
    d.s = [0.48, 0.35 + (t / 32) * 1.1, 0.48]; d.q = [0, 0, 0];
    d.c = t > 24 ? 1 : 0.5;
  });
  return f;
}

function titleAnomaly() {
  const r = rng(23), f = mk();
  f.forEach((d, i) => {
    if (i < 8) {
      const a = (i / 8) * Math.PI * 2;
      d.p = [Math.cos(a) * 8.2, Math.sin(a) * 5.4, (i % 2 ? 2 : -2)];
      d.s = [1.6, 1.6, 1.6]; d.q = [r(), r(), r()]; d.c = 0;
    } else {
      const a = i * 2.399, rr = 1.2 + (i % 9) * 0.18;
      d.p = [Math.cos(a) * rr, (i % 13 - 6) * 0.18, Math.sin(a) * rr];
      d.s = [0.38, 0.38, 0.38]; d.q = [0, 0, 0]; d.c = 1;
    }
  });
  return f;
}

function titlePmf() {
  const f = mk();
  f.forEach((d, i) => {
    const g = i % 5, k = Math.floor(i / 5);
    const a = k * 2.399, y = 1 - 2 * ((k % 44) / 44);
    if (g === 4) {
      const rr = Math.sqrt(Math.max(0, 1 - y * y)) * 1.8;
      d.p = [Math.cos(a) * rr, y * 1.8, Math.sin(a) * rr];
      d.s = [0.32, 0.32, 0.32]; d.c = 1;
    } else {
      const rr = Math.sqrt(Math.max(0, 1 - y * y)) * 1.5;
      const ox = Math.cos(g / 4 * Math.PI * 2) * 6.2;
      const oy = Math.sin(g / 4 * Math.PI * 2) * 6.2;
      d.p = [ox + Math.cos(a) * rr, oy + y * 1.5, Math.sin(a) * rr];
      d.s = [0.3, 0.3, 0.3]; d.c = 0.45 + g * 0.15;
    }
    d.q = [0, 0, 0];
  });
  return f;
}

function titleOverview() {
  const f = mk();
  f.forEach((d, i) => {
    const g = i % 6, k = Math.floor(i / 6);
    if (g === 0) {
      const src = k % 4, n = Math.floor(k / 4);
      d.p = [-13.2 + (n % 4 - 1.5) * 0.52, 5.1 - src * 3.35 + (Math.floor(n / 4) % 3 - 1) * 0.42, 0];
      d.s = [0.42, 0.42, 0.42]; d.c = 0.28;
    } else if (g === 1) {
      const layer = k % 5, n = Math.floor(k / 5);
      d.p = [-6.4 + (n % 6 - 2.5) * 0.52, (2 - layer) * 1.12, (Math.floor(n / 6) % 3 - 1) * 0.38];
      d.s = [0.68, 0.26, 0.68]; d.c = 0.5 + layer * 0.1;
    } else if (g < 5) {
      const m = g - 2;
      const a = k * 2.399, y = 1 - 2 * ((k % 37) / 37);
      const rr = Math.sqrt(Math.max(0, 1 - y * y)) * 1.45;
      d.p = [2.4 + Math.cos(a) * rr, (1 - m) * 4.5 + y * 1.45, Math.sin(a) * rr];
      d.s = [0.3, 0.3, 0.3]; d.c = 0.72;
    } else {
      const a = k * 2.399, y = 1 - 2 * ((k % 37) / 37);
      const rr = Math.sqrt(Math.max(0, 1 - y * y)) * 2.15;
      d.p = [10.8 + Math.cos(a) * rr, y * 2.15, Math.sin(a) * rr];
      d.s = [0.36, 0.36, 0.36]; d.c = 1;
    }
    d.q = [0, 0, 0];
  });
  return f;
}

function formationsFabric() {
  const r = rng(7), F = [titleFabric()];
  let f = mk();
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    const c = [[-8, 3.4, -2], [8, 3.6, 2], [-8, -3.6, 2], [8, -3.4, -2]][g];
    if (g === 0) {
      d.p = [c[0] + (r() - .5) * 4.8, c[1] + (r() - .5) * 3.6, c[2] + (r() - .5) * 3];
      d.s = [.7 + r() * .8, .7 + r() * .8, .7 + r() * .8]; d.q = [0, 0, 0];
    } else if (g === 1) {
      d.p = [c[0] + (r() - .5) * 2.2, c[1] + (k % 14 - 7) * 0.32, c[2] + (r() - .5) * 1.1];
      d.s = [2.2 + r() * 2.4, 0.16, 0.22]; d.q = [0, 0, 0];
    } else if (g === 2) {
      d.p = [c[0] + (k % 7 - 3) * .85, c[1] + (Math.floor(k / 7) - 3) * .5, c[2]];
      d.s = [1.6, .22, 1.1]; d.q = [0, 0, 0];
    } else {
      d.p = [c[0] + (r() - .5) * 5, c[1] + (r() - .5) * 4, c[2] + (r() - .5) * 4];
      d.s = [.25, .25, .25]; d.q = [r() * 3, r() * 3, r() * 3];
    }
    d.c = 0;
  });
  F.push(f);
  f = mk();
  const CX = 16;
  f.forEach((d, i) => {
    const cx = i % CX, cy = Math.floor(i / CX);
    d.p = [(cx - (CX - 1) / 2) * 1.28, ((CX - 1) / 2 - cy * 1.7) * .95, 0];
    d.s = [.86, .86, .86]; d.q = [0, 0, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const lane = i % 4, k = Math.floor(i / 4);
    d.p = [-13 + (k % 20) * 1.05, [4.2, 1.4, -1.4, -4.2][lane], (Math.floor(k / 20) - 1) * 1.1];
    d.s = [.94, .58, .58]; d.q = [0, 0, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const cx = i % CX, cy = Math.floor(i / CX);
    d.p = [(cx - (CX - 1) / 2) * 1.34, ((CX - 1) / 2 - cy * 1.7) * 1.0, 0];
    d.s = [.8, .8, 2.9 + (i % 3) * .5]; d.q = [0, 0, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3);
    if (g === 0) { const a = k / 75 * Math.PI * 2; d.p = [-10 + Math.cos(a) * 4, Math.sin(a) * 4, Math.sin(a * 3) * .8]; d.s = [.6, .6, .6]; d.q = [0, 0, a]; }
    else if (g === 1) { const lv = k % 12; d.p = [0.5, -5.5 + lv * 1.0, (Math.floor(k / 12) - 3) * .1]; d.s = [5.2, .34, 5.2]; d.q = [0, 0, 0]; }
    else { const a = k * 2.399, y = 1 - 2 * (k / 75), rr = Math.sqrt(Math.max(0, 1 - y * y)) * 4.2; d.p = [11 + Math.cos(a) * rr, y * 4.2, Math.sin(a) * rr]; d.s = [.34, .34, .34]; d.q = [a, a, 0]; }
    d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const ray = i % 5, k = Math.floor(i / 5), a = ray / 5 * Math.PI * 2 + .3;
    const dist = 2.4 + (k / 44) * 11;
    d.p = [Math.cos(a) * dist, Math.sin(a) * dist * .82, (r() - .5) * .6];
    d.s = [1.15, .34, .34]; d.q = [0, 0, a]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    if (g === 0) {
      const cx = k % 6, cy = Math.floor(k / 6);
      d.p = [-6.2 + (cx - 2.5) * .7, 3.6 + (3 - cy) * .7, 0];
      d.s = [.55, .55, .55]; d.q = [0, 0, 0];
    } else if (g === 1) {
      const lane = k % 3;
      d.p = [5.2 + (Math.floor(k / 3) % 8 - 3.5) * .55, 3.8 + (lane - 1) * 1.1, 0];
      d.s = [.85, .32, .32]; d.q = [0, 0, 0];
    } else if (g === 2) {
      const a = k * 2.399, y = 1 - 2 * ((k % 56) / 56), rr = Math.sqrt(Math.max(0, 1 - y * y)) * 2.2;
      d.p = [-6.2 + Math.cos(a) * rr, -3.6 + y * 2.2, Math.sin(a) * rr];
      d.s = [.28, .28, .28]; d.q = [a, 0, 0];
    } else {
      const ray = k % 5, a = ray / 5 * Math.PI * 2, dist = .8 + (Math.floor(k / 5) / 11) * 3.2;
      d.p = [6.2 + Math.cos(a) * dist, -3.6 + Math.sin(a) * dist * .8, 0];
      d.s = [.9, .22, .22]; d.q = [0, 0, a];
    }
    d.c = 1;
  });
  F.push(f);
  return F;
}

function formationsXai() {
  const F = [titleXai()];
  let f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3);
    if (g === 0) {
      d.p = [-9, (k % 18 - 8.5) * .55, (Math.floor(k / 18) - 1) * .8];
      d.s = [4.2, .22, .35]; d.q = [0, 0, 0]; d.c = 0;
    } else if (g === 1) {
      d.p = [(k % 6 - 2.5) * 1.1, 4.2 + (Math.floor(k / 6) % 4 - 1.5) * 1.1, 0];
      d.s = [.85, .85, .85]; d.q = [0, 0, 0]; d.c = .55;
    } else {
      d.p = [8.5 + (k % 5 - 2) * .95, -2.4 + (Math.floor(k / 5) % 6 - 2.5) * .45, 0];
      d.s = [.8, .22, 1.4]; d.q = [0, 0, 0]; d.c = .55;
    }
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3);
    const t = (k % 74) / 73;
    const x0 = (g - 1) * 9;
    d.p = [x0 * (1 - t), (g - 1) * 1.2 * (1 - t), t * 10 - 5];
    d.s = [.55, .55, .9]; d.q = [0, 0, 0]; d.c = t > .7 ? 1 : .4;
  });
  F.push(f);
  f = mk();
  const hts = [4.6, 2.3, 4.1, 3.4];
  f.forEach((d, i) => {
    const col = i % 4, k = Math.floor(i / 4);
    d.p = [(col - 1.5) * 4.6, -2.2 + (k % 14) * .52, (Math.floor(k / 14) - 1) * .8];
    d.s = [.9, .42, .9]; d.q = [0, 0, 0]; d.c = .4 + hts[col] / 8;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const rank = i % 16, k = Math.floor(i / 16);
    const h = 1.1 + (15 - rank) * .42;
    d.p = [(rank - 7.5) * 1.35, h / 2 - 2.4, (k - 6) * .35];
    d.s = [.72, h, .72]; d.q = [0, 0, 0]; d.c = rank < 5 ? 1 : .45;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3), cx = k % 7, cy = Math.floor(k / 7);
    const ox = (g - 1) * 9.2;
    d.p = [ox + (cx - 3) * .85, (3 - cy) * .85, 0];
    d.s = [.62, .62, .62]; d.q = [0, 0, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    const ox = (g % 2 ? 6 : -6), oy = (g < 2 ? 4 : -4);
    if (g === 0) {
      const step = k % 8;
      d.p = [ox + step * .55 - 2, oy - 1.5 + step * .45, 0];
      d.s = [.9, .28, .7]; d.q = [0, 0, 0];
    } else if (g === 1) {
      const a = (k / 55) * Math.PI * 2;
      d.p = [ox + Math.cos(a) * 2.4, oy + Math.sin(a) * 2.4, 0];
      d.s = [.9, .28, .28]; d.q = [0, 0, a];
    } else if (g === 2) {
      d.p = [ox, oy - 2 + (k % 10) * .42, 0];
      d.s = [2.4, .28, 1.2]; d.q = [0, 0, 0];
    } else {
      const a = (k / 55) * Math.PI * 2;
      d.p = [ox + Math.cos(a) * 2.8, oy + Math.sin(a) * 2.0, 0];
      d.s = [.4, .4, .4]; d.q = [0, 0, 0];
    }
    d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const ray = i % 4, k = Math.floor(i / 4), a = ray / 4 * Math.PI * 2 + .2;
    const dist = 2.2 + (k / 55) * 10;
    d.p = [Math.cos(a) * dist, Math.sin(a) * dist * .78, 0];
    d.s = [1.2, .32, .32]; d.q = [0, 0, a]; d.c = 1;
  });
  F.push(f);
  return F;
}

function formationsForecast() {
  const r = rng(19), F = [titleForecast()];
  let f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3);
    if (g === 0) {
      const t = k % 28;
      d.p = [(t - 13.5) * .7, -1.2 + Math.abs(Math.sin(t * .4)) * 2.4, 0];
      d.s = [.45, .4 + (t / 28) * 2.2, .45]; d.q = [0, 0, 0]; d.c = .7;
    } else if (g === 1) {
      d.p = [(k % 8 - 3.5) * 2.2, 4.4, (Math.floor(k / 8) - 2) * 1.2];
      d.s = [1.1, 1.1, 1.1]; d.q = [0, 0, 0]; d.c = .7;
    } else {
      d.p = [(k % 40 - 19.5) * .55, -4.6, (Math.floor(k / 40) - 1) * .4];
      d.s = [.28, .28, .28]; d.q = [0, r() * 2, 0]; d.c = 0;
    }
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const t = i % 28, row = Math.floor(i / 28);
    d.p = [(t - 13.5) * .85, (3 - row) * 1.5, 0];
    d.s = [.55, .55, .55 + (t / 28) * 1.4]; d.q = [0, 0, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const layer = i % 5, k = Math.floor(i / 5);
    d.p = [(k % 12 - 5.5) * 1.2, (2 - layer) * 1.55, (Math.floor(k / 12) - 1.5) * 1.1];
    d.s = [1.0, .28, .7]; d.q = [0, 0, 0]; d.c = .35 + layer * .14;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const band = i % 3, k = Math.floor(i / 3);
    d.p = [(k % 30 - 14.5) * .72, [-3.2, 0, 3.2][band] + Math.sin(k * .35) * .35, 0];
    d.s = [.55, .22 + band * .18, 3.4]; d.q = [0, 0, 0]; d.c = [.45, 1, .45][band];
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3);
    const a = k / 74 * Math.PI * 2;
    const rad = [3.2, 4.4, 2.4][g];
    d.p = [(g - 1) * 8.4 + Math.cos(a) * rad, Math.sin(a) * rad, 0];
    d.s = [.5, .5, .5]; d.q = [0, 0, a]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const row = i % 4, k = Math.floor(i / 4);
    const len = [18, 14, 9, 6][row];
    const t = k % 20;
    d.p = [(t - 9.5) * .85, (1.5 - row) * 2.0, 0];
    d.s = t < len ? [.7, .55, .7] : [.15, .15, .15];
    d.q = [0, 0, 0]; d.c = t < len ? 1 : .2;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const col = i % 4, row = Math.floor(i / 4) % 8, z = Math.floor(i / 32);
    d.p = [(col - 1.5) * 5.2, (3.2 - row * .95), z * .4];
    d.s = [3.6, .28, .7]; d.q = [0, 0, 0]; d.c = row === 0 ? 1 : .5;
  });
  F.push(f);
  return F;
}

function formationsAnomaly() {
  const r = rng(23), F = [titleAnomaly()];
  let f = mk();
  f.forEach((d, i) => {
    d.p = [(r() - .5) * 22, (r() - .5) * 12, (r() - .5) * 10];
    d.s = [.25 + r() * .7, .25 + r() * .7, .25 + r() * .7];
    d.q = [r() * 3, r() * 3, r() * 3]; d.c = 0;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const t = (i / N) * 2 - 1;
    const pinch = .35 + Math.abs(t) * 3.6;
    const a = i * 2.399;
    d.p = [Math.cos(a) * pinch, t * 6.2, Math.sin(a) * pinch];
    d.s = [.42, .42, .42]; d.q = [0, a, 0]; d.c = Math.abs(t) < .25 ? 1 : .4;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const outlier = i % 9 === 0;
    if (outlier) {
      d.p = [(r() - .5) * 20, (r() - .5) * 12, (r() - .5) * 8];
      d.s = [1.3, 1.3, 1.3]; d.q = [r(), r(), r()]; d.c = 0;
    } else {
      const a = i * .28; const rr = 2.2 + (i % 7) * .35;
      d.p = [Math.cos(a) * rr, (i % 11 - 5) * .35, Math.sin(a) * rr];
      d.s = [.45, .45, .45]; d.q = [0, 0, 0]; d.c = 1;
    }
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const rank = i % 12, k = Math.floor(i / 12);
    const h = .8 + (11 - rank) * .48;
    d.p = [(rank - 5.5) * 1.55, h / 2 - 2, (k - 8) * .28];
    d.s = [.85, h, .85]; d.q = [0, 0, 0]; d.c = rank < 4 ? 1 : .4;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const ray = i % 3, k = Math.floor(i / 3), a = ray / 3 * Math.PI * 2 - .4;
    const dist = 1.8 + (k / 74) * 12;
    d.p = [Math.cos(a) * dist, Math.sin(a) * dist * .7, 0];
    d.s = [1.25, .3, .3]; d.q = [0, 0, a]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const row = i % 8, col = Math.floor(i / 8);
    d.p = [(col % 14 - 6.5) * .95, (3.5 - row) * .85, Math.floor(col / 14) * .4];
    d.s = [.85, .22, .4]; d.q = [0, 0, 0]; d.c = row < 2 ? 1 : .4;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const row = i % 6, col = Math.floor(i / 6) % 6, z = Math.floor(i / 36);
    d.p = [(col - 2.5) * 2.4, (2.5 - row) * 1.15, z * .5];
    d.s = [1.9, .32, .7]; d.q = [0, 0, 0]; d.c = row === 0 ? 1 : .5;
  });
  F.push(f);
  return F;
}

function formationsPmf() {
  const F = [titlePmf()];
  let f = mk();
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    if (g === 0) {
      const cx = k % 8, cy = Math.floor(k / 8);
      d.p = [-8 + (cx - 3.5) * .7, 3.2 + (3 - cy) * .7, 0];
      d.s = [.58, .58, .12]; d.q = [0, 0, 0];
    } else if (g === 1) {
      d.p = [8, 3.4 + (k % 12 - 5.5) * .42, (Math.floor(k / 12) - 1) * .5];
      d.s = [3.6, .18, .28]; d.q = [0, 0, 0];
    } else if (g === 2) {
      d.p = [-8 + (k % 5 - 2) * 1.2, -3.6, (Math.floor(k / 5) - 2) * 1.1];
      d.s = [.9, .9, .9]; d.q = [0, 0, 0];
    } else {
      const t = k / 55;
      d.p = [4 + t * 6, -4 + Math.sin(t * 8) * 1.4, Math.cos(t * 8) * 1.2];
      d.s = [.32, .32, .32]; d.q = [0, t, 0];
    }
    d.c = g / 4;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    const a = k * 2.399, y = 1 - 2 * ((k % 56) / 56), rr = Math.sqrt(Math.max(0, 1 - y * y)) * 2.4;
    const ox = (g % 2 ? 6.2 : -6.2), oy = (g < 2 ? 3.4 : -3.4);
    d.p = [ox + Math.cos(a) * rr, oy + y * 2.4, Math.sin(a) * rr];
    d.s = [.32, .32, .32]; d.q = [a, 0, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const a = i * 2.399, y = 1 - 2 * (i / N), rr = Math.sqrt(Math.max(0, 1 - y * y)) * 5.4;
    d.p = [Math.cos(a) * rr, y * 5.4, Math.sin(a) * rr];
    d.s = [.38, .38, .38]; d.q = [a, a, 0]; d.c = 1;
  });
  F.push(f);
  f = mk();
  const ws = [0.30, 0.30, 0.25, 0.15];
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    const a = k / 55 * Math.PI * 2;
    const rad = 1.4 + ws[g] * 12;
    d.p = [Math.cos(g / 4 * Math.PI * 2) * 7.2 + Math.cos(a) * rad * .25, Math.sin(g / 4 * Math.PI * 2) * 7.2 + Math.sin(a) * rad * .25, 0];
    d.s = [.28 + ws[g] * 2.2, .28 + ws[g] * 2.2, .28 + ws[g] * 2.2]; d.q = [0, 0, 0]; d.c = ws[g] * 2.4;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const g = i % 5, k = Math.floor(i / 5);
    if (g < 3) {
      const a = k * 2.399, y = 1 - 2 * ((k % 44) / 44), rr = Math.sqrt(Math.max(0, 1 - y * y)) * 3.6;
      d.p = [Math.cos(a) * rr, y * 3.6, Math.sin(a) * rr];
      d.s = [.32, .32, .32]; d.q = [0, a, 0]; d.c = 1;
    } else {
      const a = k / 44 * Math.PI * 2;
      d.p = [Math.cos(a) * 7.4, Math.sin(a) * 7.4 * .55, (g === 4 ? 1.2 : -1.2)];
      d.s = [.7, .28, .28]; d.q = [0, 0, a]; d.c = .45;
    }
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const metric = i % 4, k = Math.floor(i / 4);
    const after = k % 2 === 1;
    const h = after ? [4.6, 5.2, 4.1, 3.8][metric] : [3.2, 2.1, 1.4, 1.1][metric];
    d.p = [(metric - 1.5) * 4.4 + (after ? .7 : -.7), h / 2 - 2, (Math.floor(k / 2) - 13) * .2];
    d.s = [.55, h, .55]; d.q = [0, 0, 0]; d.c = after ? 1 : .35;
  });
  F.push(f);
  f = mk();
  f.forEach((d, i) => {
    const side = i < N / 2 ? -1 : 1;
    const k = i % (N / 2);
    const cx = k % 8, cy = Math.floor(k / 8);
    d.p = [side * 7.4 + (cx - 3.5) * .85, (4 - cy) * .85, 0];
    d.s = [.55, .55, .55]; d.q = [0, 0, 0]; d.c = side > 0 ? 1 : .35;
  });
  F.push(f);
  return F;
}

function formationsOverview() {
  const r = rng(41), F = [titleOverview()];
  const W = [MODEL_W.market, MODEL_W.xai, MODEL_W.forecast, MODEL_W.anomaly];
  const H = PMF_SCORES.map((s) => 1.15 + (s.pmf_score / 100) * 7.1);

  let f = mk();
  f.forEach((d, i) => {
    const g = i % 5, k = Math.floor(i / 5);
    if (g < 4) {
      const c = [[-10.5, 5.2, 0], [-10.5, 1.7, 0], [-10.5, -1.8, 0], [-10.5, -5.3, 0]][g];
      if (g === 0) {
        d.p = [c[0] + (k % 5 - 2) * 0.7, c[1] + (Math.floor(k / 5) % 4 - 1.5) * 0.55, 0];
        d.s = [0.55, 0.55, 0.55]; d.q = [0, 0, 0];
      } else if (g === 1) {
        d.p = [c[0] + (r() - 0.5) * 3.4, c[1] + (k % 10 - 4.5) * 0.28, (r() - 0.5) * 0.8];
        d.s = [1.8 + r() * 1.6, 0.16, 0.2]; d.q = [0, 0, 0];
      } else if (g === 2) {
        d.p = [c[0] + (k % 6 - 2.5) * 0.7, c[1] + (Math.floor(k / 6) % 4 - 1.5) * 0.42, 0];
        d.s = [1.15, 0.18, 0.7]; d.q = [0, 0, 0];
      } else {
        d.p = [c[0] + (r() - 0.5) * 3.2, c[1] + (r() - 0.5) * 1.6, (r() - 0.5) * 1.4];
        d.s = [0.24, 0.24, 0.24]; d.q = [r() * 2, r() * 2, r() * 2];
      }
      d.c = 0.25;
    } else {
      d.p = [4.8, (k % 22 - 10.5) * 0.52, (Math.floor(k / 22) - 1) * 0.35];
      d.s = [0.32, 0.42, 0.32]; d.q = [0, 0, 0]; d.c = 1;
    }
  });
  F.push(f);

  f = mk();
  f.forEach((d, i) => {
    const layer = i % 5, k = Math.floor(i / 5);
    d.p = [(k % 14 - 6.5) * 0.92, (2 - layer) * 1.55, (Math.floor(k / 14) - 1) * 0.55];
    d.s = [0.78, 0.28, 0.78]; d.q = [0, 0, 0]; d.c = 0.35 + layer * 0.16;
  });
  F.push(f);

  f = mk();
  f.forEach((d, i) => {
    const g = i % 3, k = Math.floor(i / 3);
    if (g === 0) {
      const rank = k % 12;
      const h = 0.7 + (11 - rank) * 0.38;
      d.p = [-9.2 + (rank - 5.5) * 0.72, h / 2 - 0.4, (Math.floor(k / 12) - 3) * 0.28];
      d.s = [0.52, h, 0.52]; d.q = [0, 0, 0]; d.c = rank < 4 ? 1 : 0.4;
    } else if (g === 1) {
      const t = k % 28;
      const wave = Math.sin(t * 0.38) * 1.6 + t * 0.03;
      d.p = [(t - 13.5) * 0.42, wave, (Math.floor(k / 28) - 1) * 0.4];
      d.s = [0.32, 0.28 + (t / 28) * 0.9, 0.32]; d.q = [0, 0, 0]; d.c = t > 20 ? 1 : 0.5;
    } else {
      if (k % 11 === 0) {
        const a = (k / 74) * Math.PI * 2;
        d.p = [9.4 + Math.cos(a) * 3.4, Math.sin(a) * 2.4, (k % 2 ? 1.2 : -1.2)];
        d.s = [0.85, 0.85, 0.85]; d.q = [r(), r(), r()]; d.c = 0;
      } else {
        const a = k * 2.399, rr = 0.9 + (k % 7) * 0.16;
        d.p = [9.4 + Math.cos(a) * rr, (k % 11 - 5) * 0.16, Math.sin(a) * rr];
        d.s = [0.28, 0.28, 0.28]; d.q = [0, 0, 0]; d.c = 1;
      }
    }
  });
  F.push(f);

  f = mk();
  f.forEach((d, i) => {
    const lane = i % 3, k = Math.floor(i / 3);
    const t = (k % 74) / 73;
    const y0 = (1 - lane) * 4.3;
    const x = -12 + t * 18;
    const y = t < 0.62 ? y0 : y0 * (1 - (t - 0.62) / 0.38);
    d.p = [x, y, (lane - 1) * 0.45];
    d.s = [0.82, 0.34, 0.34]; d.q = [0, 0, 0]; d.c = t > 0.78 ? 1 : 0.42 + lane * 0.12;
  });
  F.push(f);

  f = mk();
  f.forEach((d, i) => {
    const g = i % 5, k = Math.floor(i / 5);
    if (g === 4) {
      const a = k * 2.399, y = 1 - 2 * ((k % 44) / 44);
      const rr = Math.sqrt(Math.max(0, 1 - y * y)) * 1.7;
      d.p = [Math.cos(a) * rr, y * 1.7, Math.sin(a) * rr];
      d.s = [0.34, 0.34, 0.34]; d.q = [0, 0, 0]; d.c = 1;
    } else {
      const a = k / 44 * Math.PI * 2;
      const rad = 2.1 + W[g] * 14;
      const ox = Math.cos(g / 4 * Math.PI * 2) * 6.8;
      const oy = Math.sin(g / 4 * Math.PI * 2) * 6.8;
      d.p = [ox + Math.cos(a) * rad * 0.18, oy + Math.sin(a) * rad * 0.18, 0];
      d.s = [0.26 + W[g] * 2.4, 0.26 + W[g] * 2.4, 0.26 + W[g] * 2.4];
      d.q = [0, 0, 0]; d.c = 0.35 + W[g] * 2.1;
    }
  });
  F.push(f);

  f = mk();
  f.forEach((d, i) => {
    const g = i % 4, k = Math.floor(i / 4);
    const h = H[g];
    d.p = [(g - 1.5) * 4.6, h / 2 - 2.6, (Math.floor(k / 14) - 1) * 0.35];
    if (k % 14 === 0) {
      d.s = [1.15, h, 1.15];
    } else {
      d.p[1] = -2.6 + ((k % 14) / 13) * h;
      d.s = [0.72, 0.28, 0.72];
    }
    d.q = [0, 0, 0]; d.c = 0.38 + (H[g] / 9);
  });
  F.push(f);

  f = mk();
  f.forEach((d, i) => {
    const g = i % 6, k = Math.floor(i / 6);
    const cx = (g - 2.5) * 5.15;
    d.p = [cx + (k % 5 - 2) * 0.38, (Math.floor(k / 5) % 5 - 2) * 0.38, 0];
    d.s = [0.42, 0.42, 0.42]; d.q = [0, 0, 0];
    d.c = g === 5 ? 1 : 0.32 + g * 0.1;
  });
  F.push(f);
  return F;
}

export const MODELS = {
  overview: { formations: formationsOverview, CAM: [42, 34, 32, 36, 38, 32, 30, 40], CLOUD: [
    { o: .08, c: [0, 0, 0], s: 1.15 },
    { o: 0, c: [0, 0, 0], s: 1 }, { o: .1, c: [0, 0, 0], s: 1 }, { o: .12, c: [0, 0, 0], s: 1 },
    { o: .22, c: [6, 0, 0], s: .9 }, { o: .45, c: [0, 0, 0], s: .85 },
    { o: .08, c: [0, 0, 0], s: 1 }, { o: .1, c: [0, 0, 0], s: 1.05 }
  ]},
  fabric: { formations: formationsFabric, CAM: [36, 32, 27, 25, 28, 34, 30, 27], CLOUD: [
    { o: .18, c: [0, 0, 0], s: 1.1 },
    { o: 0, c: [0, 0, 0], s: 1 }, { o: 0, c: [0, 0, 0], s: 1 }, { o: 0, c: [0, 0, 0], s: 1 },
    { o: .6, c: [0, 0, 0], s: 1 }, { o: .85, c: [11, 0, 0], s: .52 },
    { o: .3, c: [0, 0, 0], s: 1.05 }, { o: .12, c: [0, 0, 0], s: 1.1 }
  ]},
  xai: { formations: formationsXai, CAM: [34, 30, 28, 28, 30, 32, 30, 27], CLOUD: [
    { o: .12, c: [0, 0, 0], s: 1 },
    { o: .1, c: [0, 0, 0], s: 1 }, { o: 0, c: [0, 0, 0], s: 1 }, { o: .2, c: [0, 0, 0], s: 1 },
    { o: .45, c: [0, 0, 0], s: 1 }, { o: .12, c: [0, 0, 0], s: 1 }, { o: .1, c: [0, 0, 0], s: 1 }, { o: .2, c: [0, 0, 0], s: 1.05 }
  ]},
  forecast: { formations: formationsForecast, CAM: [34, 30, 26, 28, 29, 32, 30, 30], CLOUD: [
    { o: .1, c: [0, 0, 0], s: 1 },
    { o: .08, c: [0, 0, 0], s: 1 }, { o: 0, c: [0, 0, 0], s: 1 }, { o: .35, c: [0, 0, 0], s: 1 },
    { o: .2, c: [0, 0, 0], s: 1 }, { o: .15, c: [0, 0, 0], s: 1 }, { o: .1, c: [0, 0, 0], s: 1 }, { o: 0, c: [0, 0, 0], s: 1 }
  ]},
  anomaly: { formations: formationsAnomaly, CAM: [36, 34, 28, 32, 30, 30, 30, 29], CLOUD: [
    { o: .2, c: [0, 0, 0], s: 1.1 },
    { o: .55, c: [0, 0, 0], s: 1.15 }, { o: .2, c: [0, 0, 0], s: .8 }, { o: .7, c: [0, 0, 0], s: 1.2 },
    { o: .25, c: [0, 0, 0], s: 1 }, { o: .15, c: [0, 0, 0], s: 1 }, { o: .1, c: [0, 0, 0], s: 1 }, { o: 0, c: [0, 0, 0], s: 1 }
  ]},
  pmf: { formations: formationsPmf, CAM: [36, 32, 30, 28, 32, 30, 30, 30], CLOUD: [
    { o: .18, c: [0, 0, 0], s: 1.1 },
    { o: .15, c: [0, 0, 0], s: 1 }, { o: .5, c: [0, 0, 0], s: 1 }, { o: .85, c: [0, 0, 0], s: 1.05 },
    { o: .25, c: [0, 0, 0], s: 1 }, { o: .55, c: [0, 0, 0], s: .85 }, { o: .1, c: [0, 0, 0], s: 1 }, { o: .15, c: [0, 0, 0], s: 1 }
  ]}
};
